import React from 'react';
import clsx from 'clsx';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Link from '@material-ui/core/Link';

import './FilterTree.css';
import { FilterTreeType } from 'constants/filter';
import { noop } from 'util/function';
import Tooltip from 'components/tooltip/Tooltip';
import Tree, { TreeDataProvider } from 'components/tree/Tree';
import TooltipIcon from 'components/icon/TooltipIcon';

//
// --- Types ---

export interface FilterTreeProps {
  className?: string;
  onFilterCheckboxChange?: (
    event: React.ChangeEvent<HTMLInputElement>,
    treeNode: TransformedFilterTreeNode,
    isChecked: boolean
  ) => void;
  onFilterClearClick?: (treeNodeId: string) => void;
  /** returns the new open value as a result of the user interaction */
  onNodeDisplayChange: (treeNode: TransformedFilterTreeNode) => void;
  openTreeNodeIds?: ReadonlyArray<string>;
  treeDataProvider: InstanceType<typeof TreeDataProvider>;
}

export interface TransformedFilterTreeNode {
  canClear?: boolean;
  checked: boolean | null;
  children: ReadonlyArray<TransformedFilterTreeNode>;
  component?: React.ReactNode;
  filterChipLabel?: string | null;
  filterPrimaryKey?: string | null;
  filterTreeType: FilterTreeType | null;
  filterType?: string | null;
  id: string; // uuid v4
  isCategoryNode?: boolean;
  fieldMap?: { isWoodMacFilter?: boolean };
  leaf: boolean;
  name: string | null;
  open: boolean;
  parentId: string | null;
  showInfoIcon?: boolean;
  tooltip?: string;
}

//
// --- FilterTree Component ---

const FilterTree: React.FC<FilterTreeProps> = props => {
  const {
    className,
    onFilterCheckboxChange = noop,
    onFilterClearClick = noop,
    onNodeDisplayChange = noop,
    openTreeNodeIds = [],
    treeDataProvider
  } = props;

  /**
   * Tree Component Gotcha: using the `getLabel` prop on the Tree Component will only run the first
   * definition of the function provided to that prop. Because we use bidTeams in the label render process, and
   * that does change, we need to define getLabel on the treeDataProvider instead. Do not run
   * treeDatProvider.requestData() from here as that will cause an infinite render loop
   */
  treeDataProvider.updateConfig({
    getLabel: (nodeData: any) => {
      if (nodeData.component && React.isValidElement(nodeData.component)) {
        const { component, ...remainingNodeData } = nodeData;
        return React.cloneElement(component, { nodeData: remainingNodeData });
      }

      const clearButton = nodeData.canClear ? (
        <div className="filter-tree-clear-link-container">
          <Link
            className="filter-tree-clear-link"
            color="secondary"
            href="#"
            onClick={() => onFilterClearClick(nodeData.id)}
          >
            clear
          </Link>
        </div>
      ) : null;

      return (
        <span className="filter-tree-label-span">
          <Tooltip
            id={`filter-tree-tooltip-${nodeData.id}`}
            title={nodeData.tooltip ? nodeData.tooltip : nodeData.name}
          >
            {nodeData.checked === null ? (
              <span className="filter-tree-category-label">
                {nodeData.name}
                {clearButton}
              </span>
            ) : (
              <FormControlLabel
                className={nodeData.isCategoryNode ? 'filter-tree-category-label' : 'filter-tree-checkbox-label'}
                control={
                  <Checkbox
                    checked={nodeData.checked}
                    className="filter-tree-checkbox"
                    indeterminate={!nodeData.checked && nodeData.childChecked}
                    onChange={event => onFilterCheckboxChange(event, nodeData, event.target.checked)}
                  />
                }
                data-testid="filter-tree-label"
                label={
                  clearButton ? (
                    <>
                      {nodeData.name}
                      {clearButton}
                    </>
                  ) : (
                    nodeData.name
                  )
                }
              />
            )}
          </Tooltip>
          {nodeData.showInfoIcon && (
            <TooltipIcon
              iconClassName="filter-tree-warning-icon"
              iconName="info"
              iconTestId="filter-tree-warning-icon"
              title={nodeData.tooltip ? nodeData.tooltip : nodeData.name}
              tooltipClassName="filter-tree-tooltip"
            />
          )}
        </span>
      );
    },
    getOpen: (nodeData: any) => openTreeNodeIds.includes(nodeData.id)
  });

  return (
    <Tree
      className={clsx('filter-tree', className)}
      nodeHeight={30}
      onNodeToggle={onNodeDisplayChange}
      treeDataProvider={treeDataProvider}
    />
  );
};

export default FilterTree;

// export for easier access to provider
export { TreeDataProvider };
